import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import swapsApi from '../../api/swaps';

function formatDate(iso) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

const ROLE_LABELS  = { ON_CALL: 'On Call', SITTER: 'Sitter' };

export default function PostSwapModal({ onClose, onPosted }) {
  const queryClient        = useQueryClient();
  const [selectedShift, setSelectedShift] = useState(null);
  const [error,         setError]         = useState('');

  // Fetch this SL's upcoming published shift assignments
  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ['my-upcoming-shifts'],
    queryFn:  swapsApi.myUpcomingShifts,
  });

  // Filter out shifts that already have an active (OPEN or AGREED) swap listing
  const postableShifts = shifts.filter((s) => !s.has_open_swap);

  const mutation = useMutation({
    mutationFn: (shiftId) => swapsApi.createListing({ shift_assignment: shiftId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['swaps-mine'] });
      queryClient.invalidateQueries({ queryKey: ['swaps-open'] });
      onPosted?.();
      onClose();
    },
    onError: (err) => setError(err.data ? JSON.stringify(err.data) : err.message),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <p className="font-semibold text-gray-900">Post a Shift for Swap</p>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
          <p className="text-sm text-gray-500">
            Select one of your upcoming shifts to post to the marketplace.
            Another SL can claim it and your SSL will be notified to approve.
          </p>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {isLoading && (
            <p className="text-sm text-gray-400 text-center py-6">Loading your shifts…</p>
          )}

          {!isLoading && !shifts.length && (
            <p className="text-sm text-gray-400 text-center py-6">
              You have no upcoming published shifts.
            </p>
          )}

          {/* Shift list */}
          <div className="space-y-2">
            {postableShifts.map((shift) => {
              const isSelected = selectedShift === shift.id;
              return (
                <button
                  key={shift.id}
                  onClick={() => setSelectedShift(isSelected ? null : shift.id)}
                  className={`w-full text-left rounded-xl border px-4 py-3 transition-all ${
                    isSelected
                      ? 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-300'
                      : 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-gray-50'
                  }`}
                >
                  <p className="font-medium text-gray-900">{formatDate(shift.date)}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {ROLE_LABELS[shift.role] ?? shift.role}
                    {shift.location_name && ` · ${shift.location_name}`}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => selectedShift && mutation.mutate(selectedShift)}
            disabled={!selectedShift || mutation.isPending}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors"
          >
            {mutation.isPending ? 'Posting…' : 'Post to Marketplace'}
          </button>
        </div>
      </div>
    </div>
  );
}
